/**
 * BaseIcon.jsx
 * Created by michaelbray on 7/9/17.
 */

import React from 'react';
import PropTypes from 'prop-types';
import svg4everybody from 'svg4everybody';
import cx from 'classnames';

import IconSingleton from './iconSingleton';

export default class BaseIcon extends React.PureComponent {
    static propTypes = {
        /** Sets the icon’s width and height */
        size: PropTypes.oneOf([undefined, 'xs', 'sm', 'md', 'lg', 'xl', 'xxl']),

        /** Text that a screen reader should announce when the icon is selected */
        screenReaderText: PropTypes.string,

        /** Text that should appear in the brower-native tooltip */
        title: PropTypes.string,

        /** Adds custom class to existing classes */
        className: PropTypes.string,

        /** Internal only: Do not use */
        iconName: PropTypes.string.isRequired,

        /** Internal only: Do not use */
        iconClass: PropTypes.string.isRequired,

        /** Legacy prop: Do not use */
        alt: PropTypes.string
    };

    static defaultProps = {
        size: undefined,
        className: undefined,
        screenReaderText: undefined,
        title: undefined,
        alt: undefined
    };

    constructor(props) {
        super(props);

        this.state = {
            icon: {
                data: '',
                viewBox: '0 0 0 0'
            }
        };

        this.iconSingleton = IconSingleton;
        this.subscription = null;
    }
    componentDidMount() {
        // download icons if necessary, otherwise populate the correct state
        this.prepareIcons();

        // necessary for IE support
        svg4everybody({
            polyfill: true
        });
    }
    componentWillUnmount() {
        // unsubscribe to reduce memory overhead, if we have a subscription active
        if (this.subscription) {
            this.iconSingleton.unsubscribe(this.subscription);
        }
    }

    prepareIcons() {
        // check to see if anyone has started the download process
        if (!this.iconSingleton.svgLoaded) {
            // no icons available, subscribe to the singleton to be notified when they are ready
            this.subscription = this.iconSingleton.subscribe(this.svgEvent);
            // check to see if they are in the process of being downloaded
            if (!this.iconSingleton.svgRequested) {
                // not requested either, let's request it now
                this.iconSingleton.downloadIcons();
            }
        }
        else {
            // icons are ready
            this.displayIcon();
        }
    }

    displayIcon() {
        // set the state to the correct SVG data
        if ({}.hasOwnProperty.call(this.iconSingleton.svgCache, this.props.iconName)) {
            this.setState({
                icon: this.iconSingleton.svgCache[this.props.iconName]
            });
        }
    }

    svgEvent = () => {
        // icons have loaded, unsubscribe to reduce memory overhead
        this.iconSingleton.unsubscribe(this.subscription);
        this.subscription = null;

        // display the icon
        this.displayIcon();
    }

    render() {
        const {
            iconClass,
            className,
            alt,
            iconName,
            screenReaderText,
            title,
            size,
            ...rest
        } = this.props;

        // Use title as Screen Reader text if srText prop is not passed
        let srText = screenReaderText || title;
        let computedTitle = title;

        const classes = cx('sarsa-icon', iconClass, className, {
            'xs': size === 'xs',
            'sm': size === 'sm',
            'md': size === 'md',
            'lg': size === 'lg',
            'xl': size === 'xl',
            'xxl': size === 'xxl'
        });

        // To maintain legacy alt prop, move the value to title
        if (typeof alt === 'string' && alt.length && !title) {
            computedTitle = alt;
        }

        // To maintain legacy alt prop, move the value to screenReader
        if (typeof alt === 'string' && alt.length && !screenReaderText) {
            srText = alt;
        }

        // IE11 and Edge don't care about an SVG's viewbox and will not set
        // the correct height and width of the SVG container based upon it
        // Here, we're splitting the SVG's viewbox into an array, grabbing
        // the height and width, and then manually setting it on the SVG
        let width = 0;
        let height = 0;
        if (this.state.icon.viewBox){
            const viewbox = this.state.icon.viewBox.split(" ");
            width = viewbox[2];
            height = viewbox[3];
        }

        return (
            <svg
                data-component="Icon"
                className={classes}
                viewBox={this.state.icon.viewBox}
                key={this.state.icon.data}
                role={srText ? "img" : "presentation"}
                aria-label={srText}
                focusable="false"
                height={height}
                width={width}
                {...rest} >

                {computedTitle && (
                    <title>{computedTitle}</title>
                )}

                {/* eslint-disable react/no-danger */}
                {/* we need to write the SVG data onto the DOM */}
                <g
                    dangerouslySetInnerHTML={{ __html: this.state.icon.data }} />
                {/* eslint-enable react/no-danger */}
            </svg>
        );
    }
}
